import React, { useState, useEffect } from "react";
import { getLobCodeByLobName } from "../config/product-config";
import { getTypeOfQuote } from "../config/quote-outcomes-config";

interface EndorsementRow {
    id: string;
    lob: string;
    policyNumber: string;
    status: string;
    primaryOutcomeType?: string;
}

interface EndorsementInfoProps {
    onDataChange?: (hasEndorsements: boolean, endorsements: EndorsementRow[]) => void;
    lineOfBusinessOptions: string[];
    referenceNumber?: string;
    horizontalLayout?: boolean;
}

const MAX_ENDORSEMENTS = 5;

export const EndorsementInfo: React.FC<EndorsementInfoProps> = ({ onDataChange, lineOfBusinessOptions, referenceNumber = "", horizontalLayout = false }) => {
    const componentId = horizontalLayout ? 'main-page' : 'sidebar';
    const getBasePolicyNumber = (policyNumber: string, lob: string): string => {
        if (!policyNumber || !lob) return policyNumber;
        const lobCode = getLobCodeByLobName(lob);
        if (lobCode && policyNumber.endsWith(lobCode)) {
            return policyNumber.slice(0, -lobCode.length);
        }
        return policyNumber;
    };

    const appendLobCodeToPolicyNumber = (basePolicyNumber: string, lob: string): string => {
        if (!basePolicyNumber || !lob) return basePolicyNumber;
        const lobCode = getLobCodeByLobName(lob);
        if (lobCode && !basePolicyNumber.endsWith(lobCode)) {
            return basePolicyNumber + lobCode;
        }
        return basePolicyNumber;
    };

    const [hasEndorsements, setHasEndorsements] = useState<boolean>(false);
    const [endorsements, setEndorsements] = useState<EndorsementRow[]>([
        { id: "1", lob: "", policyNumber: "", status: "", primaryOutcomeType: "" }
    ]);
    const [hoveredEndorsementId, setHoveredEndorsementId] = useState<string | null>(null);

    const statusOptions = ["Sale", "Quote"];

    useEffect(() => {
        let refNum = "";
        if (referenceNumber !== undefined && referenceNumber !== null) {
            if (typeof referenceNumber === 'string') {
                refNum = referenceNumber;
            } else {
                refNum = String(referenceNumber);
            }
        }
        
        if (refNum && refNum.trim() !== "" && refNum.length > 0) {
            setEndorsements(prev => prev.map(endorsement => {
                const basePolicyNumber = getBasePolicyNumber(endorsement.policyNumber, endorsement.lob);
                
                const isEmpty = !endorsement.policyNumber || endorsement.policyNumber.trim() === "" || !basePolicyNumber || basePolicyNumber.trim() === "";
                
                if (isEmpty) {
                    const updatedPolicyNumber = endorsement.lob 
                        ? appendLobCodeToPolicyNumber(refNum, endorsement.lob)
                        : refNum;
                    return { ...endorsement, policyNumber: updatedPolicyNumber };
                }
                
                const isExactMatch = basePolicyNumber === refNum;
                const isTypingForward = basePolicyNumber.length > 0 && basePolicyNumber.length < refNum.length && refNum.startsWith(basePolicyNumber);
                const isBackspace = basePolicyNumber.length > 0 && refNum.length > 0 && basePolicyNumber.length > refNum.length && basePolicyNumber.startsWith(refNum);
                
                const shouldUpdate = isExactMatch || isTypingForward || isBackspace;
                
                if (shouldUpdate) {
                    const updatedPolicyNumber = endorsement.lob 
                        ? appendLobCodeToPolicyNumber(refNum, endorsement.lob)
                        : refNum;
                    return { ...endorsement, policyNumber: updatedPolicyNumber };
                }
                return endorsement;
            }));
        } else if (refNum === "" || (refNum && refNum.trim() === "")) {
            setEndorsements(prev => prev.map(endorsement => {
                const basePolicyNumber = getBasePolicyNumber(endorsement.policyNumber, endorsement.lob);
                if ((!endorsement.policyNumber || endorsement.policyNumber.trim() === "" || basePolicyNumber === "") && !endorsement.lob) {
                    return { ...endorsement, policyNumber: "" };
                }
                return endorsement;
            }));
        }
    }, [referenceNumber]);

    const handleEndorsementChange = (value: boolean) => {
        setHasEndorsements(value);
        if (onDataChange) {
            onDataChange(value, endorsements);
        }
    };

    const handleEndorsementRowChange = (id: string, field: keyof EndorsementRow, value: string) => {
        const updatedEndorsements = endorsements.map(endorsement => {
            if (endorsement.id === id) {
                if (field === "lob") {
                    const basePolicyNumber = getBasePolicyNumber(endorsement.policyNumber, endorsement.lob);
                    const newBasePolicyNumber = basePolicyNumber || referenceNumber || "";
                    const updatedPolicyNumber = value 
                        ? appendLobCodeToPolicyNumber(newBasePolicyNumber, value)
                        : newBasePolicyNumber;
                    return { ...endorsement, lob: value, policyNumber: updatedPolicyNumber };
                } else if (field === "policyNumber") {
                    return { ...endorsement, policyNumber: value };
                } else if (field === "status") {
                    return { 
                        ...endorsement, 
                        status: value,
                        primaryOutcomeType: value === "Quote" ? endorsement.primaryOutcomeType : ""
                    };
                }
                return { ...endorsement, [field]: value };
            }
            return endorsement;
        });
        setEndorsements(updatedEndorsements);
        if (onDataChange) {
            onDataChange(hasEndorsements, updatedEndorsements);
        }
    };

    const handleAddEndorsement = () => {
        if (endorsements.length >= MAX_ENDORSEMENTS) {
            return;
        }
        const newId = `endorsement-${Date.now()}`;
        const basePolicyNumber = referenceNumber || "";
        const newEndorsement: EndorsementRow = {
            id: newId,
            lob: "",
            policyNumber: basePolicyNumber,
            status: "",
            primaryOutcomeType: ""
        };
        const updatedEndorsements = [...endorsements, newEndorsement];
        setEndorsements(updatedEndorsements);
        if (onDataChange) {
            onDataChange(hasEndorsements, updatedEndorsements);
        }
    };

    const handleRemoveEndorsement = (id: string) => {
        const updatedEndorsements = endorsements.filter(endorsement => endorsement.id !== id);
        setEndorsements(updatedEndorsements);
        if (onDataChange) {
            onDataChange(hasEndorsements, updatedEndorsements);
        }
    };

    return (
        <div>
            <div>
                <form>
                    <fieldset role="radiogroup" className="form-field radio-button-group">
                        <legend>Did You Sell or Quote Endorsements?</legend>
                        <div className="endorsement-radio-container">
                            <input
                                id={`radio-endorsements-no-${componentId}`}
                                type="radio"
                                name={`hasEndorsements-${componentId}`}
                                value="no"
                                checked={hasEndorsements === false}
                                onChange={() => handleEndorsementChange(false)}
                            />
                            <label
                                className="radio endorsement-radio-label"
                                htmlFor={`radio-endorsements-no-${componentId}`}
                            >
                                <span>No</span>
                            </label>
                            <input
                                id={`radio-endorsements-yes-${componentId}`}
                                type="radio"
                                name={`hasEndorsements-${componentId}`}
                                value="yes"
                                checked={hasEndorsements === true}
                                onChange={() => handleEndorsementChange(true)}
                            />
                            <label
                                className="radio endorsement-radio-label"
                                htmlFor={`radio-endorsements-yes-${componentId}`}
                            >
                                <span>Yes</span>
                            </label>
                        </div>
                    </fieldset>
                </form>
            </div>

            {hasEndorsements && (
                <div>
                    <h3 className="endorsement-section-title">Endorsement LOB & Policy # Sold</h3>
                    <div>
                        {endorsements.map((endorsement, index) => (
                            <div
                                key={endorsement.id}
                                className="endorsement-row-container"
                            >
                                {/* First Sub-row: Line of Business + Remove Button */}
                                <div className="endorsement-sub-row endorsement-sub-row-first">
                                    <div className="select-box endorsement-flex-1">
                                        <select
                                            value={endorsement.lob || ""}
                                            onChange={(e) => handleEndorsementRowChange(endorsement.id, "lob", e.target.value)}
                                            className={`endorsement-select-input ${!endorsement.lob ? 'endorsement-select-watermark' : 'endorsement-select-filled'}`}
                                        >
                                            <option value="" disabled hidden className="endorsement-select-watermark">Select LOB</option>
                                            {lineOfBusinessOptions.map((lob, lobIndex) => (
                                                <option key={lobIndex} value={lob}>
                                                    {lob}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {endorsements.length > 1 ? (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveEndorsement(endorsement.id)}
                                            className="endorsement-remove-button"
                                            aria-label="Remove endorsement"
                                            onMouseEnter={() => setHoveredEndorsementId(endorsement.id)}
                                            onMouseLeave={() => setHoveredEndorsementId(null)}
                                        >
                                            <span className={
                                                hoveredEndorsementId === endorsement.id
                                                    ? "geico-icon icon-trash geico-icon--small"
                                                    : "geico-icon icon-trash geico-icon--small icon--gray"
                                            }></span>
                                        </button>
                                    ) : (
                                        <div className="endorsement-remove-button" style={{ visibility: 'hidden' }}>
                                            <span className="geico-icon icon-trash geico-icon--small"></span>
                                        </div>
                                    )}
                                </div>

                                <div className="endorsement-sub-row endorsement-sub-row-second">
                                    <div className="endorsement-flex-55">
                                        <input
                                            type="text"
                                            value={endorsement.policyNumber || ""}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                handleEndorsementRowChange(endorsement.id, "policyNumber", newValue);
                                            }}
                                            placeholder="Policy #"
                                            className="endorsement-input"
                                        />
                                    </div>

                                    <div className="select-box endorsement-flex-40">
                                        <select
                                            value={endorsement.status || ""}
                                            onChange={(e) => handleEndorsementRowChange(endorsement.id, "status", e.target.value)}
                                            className={`endorsement-select-input ${!endorsement.status ? 'endorsement-select-watermark' : 'endorsement-select-filled'}`}
                                        >
                                            <option value="" disabled hidden className="endorsement-select-watermark">Select Outcome</option>
                                            {statusOptions.map((status) => (
                                                <option key={status} value={status}>
                                                    {status}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {endorsement.status === "Quote" && (
                                    <div className="endorsement-sub-row" style={{ marginTop: "4px" }}>
                                        <div className="endorsement-flex-1">
                                            <label className="text" htmlFor={`primaryOutcomeType-${endorsement.id}`} style={{ marginBottom: "4px", display: "block" }}>
                                                Type Of Quote
                                            </label>
                                            <div className="select-box">
                                                <select
                                                    id={`primaryOutcomeType-${endorsement.id}`}
                                                    name="primaryOutcomeType"
                                                    value={endorsement.primaryOutcomeType || ""}
                                                    onChange={(e) => handleEndorsementRowChange(endorsement.id, "primaryOutcomeType", e.target.value)}
                                                    className={`endorsement-select-input ${!endorsement.primaryOutcomeType ? 'endorsement-select-watermark' : 'endorsement-select-filled'}`}
                                                >
                                                    <option value="" disabled hidden className="endorsement-select-watermark">Select</option>
                                                    {getTypeOfQuote("Quote").map((quoteType, quoteIndex) => (
                                                        <option key={quoteIndex} value={quoteType}>
                                                            {quoteType}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {endorsements.length < MAX_ENDORSEMENTS && (
                        <div className="endorsement-add-link-container">
                            <button
                                type="button"
                                className="link-button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleAddEndorsement();
                                }}
                            >
                                + Add Another Endorsement
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

